import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { IViewConfig } from "lola-framework-ui-test";
import type { IErrorState } from "../shared/error/interfaces/error";
import { errorInitialState, themeFormattedInitialState } from "../constants";
import type { IAmountState } from "../interfaces/amount";
import type { IPaymentState } from "../interfaces/payment";
import type { ICardState } from "../interfaces/card";
import type { IQuoteResponse } from "../services/api";
import type { FundingInstrument } from "../interfaces/funding";

interface GlobalState {
  theme: IViewConfig;
  setTheme: (theme: IViewConfig) => void;

  linkId: string | null;
  setLinkId: (linkId: string | null) => void;

  errorState: IErrorState;
  setErrorState: (errorState: IErrorState) => void;

  amountState: IAmountState;
  setAmountState: (amountState: IAmountState) => void;

  paymentState: IPaymentState;
  setPaymentState: (paymentState: IPaymentState) => void;

  cardState: ICardState;
  setCardState: (cardState: ICardState) => void;

  cardList: ICardState[];
  setCardList: (cardList: ICardState[]) => void;

  quoteState: IQuoteResponse | null;
  setQuoteState: (quoteState: IQuoteResponse | null) => void;

  /** Saved funding card. Register happens at add-card; rail prepare on confirm. */
  fundingInstrument: FundingInstrument | null;
  setFundingInstrument: (fundingInstrument: FundingInstrument | null) => void;

  /** Set after successful prepareFundingRail on confirm (CARD only). */
  preparedFundingPaymentMethodId: string | null;
  setPreparedFundingPaymentMethodId: (id: string | null) => void;

  /** Canonical funding method pinned across add-card / instrument pick. */
  pendingFundingMethod: string | null;
  setPendingFundingMethod: (method: string | null) => void;
}

export const useStore = create<GlobalState>()(
  persist(
    (set) => ({
      theme: themeFormattedInitialState,
      setTheme: (theme) => {
        set(() => ({ theme }));
      },

      linkId: null,
      setLinkId: (linkId) => {
        set(() => ({ linkId }));
      },

      errorState: errorInitialState,
      setErrorState: (errorState) => {
        set(() => ({ errorState }));
      },

      amountState: {
        amount: "",
      },
      setAmountState: (amountState) => {
        set(() => ({ amountState }));
      },

      paymentState: {
        payment: null,
      },
      setPaymentState: (paymentState) => {
        set(() => ({ paymentState }));
      },

      cardState: {
        cardId: null,
        name: null,
        brand: null,
        last4: null,
        accountNumber: null,
        expirationDate: null,
        fullName: null,
        cvv: null,
      },
      setCardState: (cardState) => {
        set(() => ({ cardState }));
      },

      cardList: [],
      setCardList: (cardList) => {
        set(() => ({ cardList }));
      },

      quoteState: null,
      setQuoteState: (quoteState) => {
        set(() => ({ quoteState }));
      },

      fundingInstrument: null,
      setFundingInstrument: (fundingInstrument) => {
        set(() => ({ fundingInstrument }));
      },

      preparedFundingPaymentMethodId: null,
      setPreparedFundingPaymentMethodId: (preparedFundingPaymentMethodId) => {
        set(() => ({ preparedFundingPaymentMethodId }));
      },

      pendingFundingMethod: null,
      setPendingFundingMethod: (pendingFundingMethod) => {
        set(() => ({ pendingFundingMethod }));
      },
    }),
    {
      name: "raas-widgets-flow-lola-commit",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        quoteState: state.quoteState,
        preparedFundingPaymentMethodId: state.preparedFundingPaymentMethodId,
      }),
    },
  ),
);
